import React from 'react';
import { Box } from '@mui/material';

// The TopoSDS wordmark.
//
// It replaces the blue "T" square that stood in for a logo in the rail and
// on every auth screen. Two files, both trimmed from the same source the
// mobile app ships as its icon:
//
//   logo.png          the mark as drawn — black wordmark, for light surfaces
//   logo-reverse.png  the same mark with the black ink recoloured white, for
//                     dark surfaces. The three lights keep their own colours
//                     because red/amber/green read correctly on black; only
//                     the "Topo" letters needed to change, and without this
//                     they were invisible against #100F0A.
//
// Sized by HEIGHT. The lockup is 2.18:1 and always keeps that ratio, so a
// caller never has to think about the width.
//
// Which file gets used is decided here rather than at the call site, because
// there are two different questions and they were being answered by one prop.
//
//   "Am I on a surface that is always dark?"  — the auth showcase panel, which
//   is nav.bg in both schemes. That is what `reverse` means, and it is still
//   set by hand, because no amount of scheme-watching can know it.
//
//   "Am I on a surface that follows the theme?" — the topbar, the public
//   header and footer, the auth screen's phone layout. All four are
//   `background.paper`, which used to be #FFFFFF and now goes to #1E1D17 in
//   dark mode. They asked for the black wordmark and kept getting it, so the
//   logo went invisible the moment dark mode landed.
//
// The second case is answered by <picture> and a media query rather than by
// reading the scheme in JS. Three reasons: it resolves during the first paint
// so there is no flash of the wrong mark, it costs no re-render, and it keys
// off exactly the same signal the theme does (`colorSchemeSelector: 'media'`),
// so the two cannot drift apart.

const RATIO = 886 / 406;

const Logo: React.FC<{
  height: number;
  /** The surface is dark in BOTH schemes — the auth panel, a dark rail. */
  reverse?: boolean;
  /** Rendered inside a link or button — drop the alt text to avoid a double label. */
  decorative?: boolean;
}> = ({ height, reverse, decorative }) => {
  const sizing = {
    height,
    width: Math.round(height * RATIO),
    display: 'block',
    objectFit: 'contain',
    userSelect: 'none',
  } as const;

  const alt = decorative ? '' : 'TopoSDS';
  const url = (file: string) => `${process.env.PUBLIC_URL}/${file}`;

  if (reverse) {
    return (
      <Box
        component="img"
        src={url('logo-reverse.png')}
        alt={alt}
        aria-hidden={decorative || undefined}
        sx={sizing}
        draggable={false}
      />
    );
  }

  return (
    // `lineHeight: 0` because <picture> is an inline box and would otherwise
    // add a descender's worth of space under the mark, which is enough to
    // knock it off centre in the topbar.
    <Box component="picture" sx={{ display: 'block', lineHeight: 0 }}>
      <source srcSet={url('logo-reverse.png')} media="(prefers-color-scheme: dark)" />
      <Box
        component="img"
        src={url('logo.png')}
        alt={alt}
        aria-hidden={decorative || undefined}
        sx={sizing}
        draggable={false}
      />
    </Box>
  );
};

export default Logo;
